import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Shield, ShoppingCart, FileDown, Loader2 } from 'lucide-react';
import { ArticleInventaire } from '@/hooks/useInventaire';
import { Bateau } from '@/hooks/useBateaux';
import { EspaceStockage } from '@/hooks/useEspacesStockage';
import jsPDF from 'jspdf';
import { toast } from '@/components/ui/use-toast';

interface InventairePDFDialogProps {
  articles: ArticleInventaire[];
  bateau?: Bateau;
  espacesStockage?: EspaceStockage[];
  nomBateau: string;
}

// Couleurs du site
const NAUTIC_DARK = [20, 26, 51] as const;   // #141A33
const NAUTIC_GOLD = [234, 179, 8] as const;   // #EAB308
const WHITE = [255, 255, 255] as const;

const InventairePDFDialog = ({ articles, bateau, espacesStockage = [], nomBateau }: InventairePDFDialogProps) => {
  const [generating, setGenerating] = useState<string | null>(null);

  const addHeader = (doc: jsPDF, title: string) => {
    const pageWidth = doc.internal.pageSize.width;
    
    // Bandeau haut
    doc.setFillColor(...NAUTIC_DARK);
    doc.rect(0, 0, pageWidth, 40, 'F');
    
    // Ligne or décorative
    doc.setFillColor(...NAUTIC_GOLD);
    doc.rect(0, 40, pageWidth, 3, 'F');
    
    // Logo
    doc.setTextColor(...NAUTIC_GOLD);
    doc.setFontSize(20);
    doc.setFont(undefined, 'bold');
    doc.text('NAUTICOACH', 20, 18);
    
    // Sous-titre
    doc.setTextColor(...WHITE);
    doc.setFontSize(9);
    doc.setFont(undefined, 'normal');
    doc.text('Votre assistant nautique', 20, 26);
    
    // Titre du document
    doc.setTextColor(...WHITE);
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text(title, pageWidth - 20, 25, { align: 'right' });
    
    return 55;
  };

  const addBoatInfo = (doc: jsPDF, yStart: number): number => {
    if (!bateau) return yStart;
    const pageWidth = doc.internal.pageSize.width;
    let y = yStart;
    
    // Cadre info bateau
    doc.setFillColor(245, 247, 250);
    doc.roundedRect(15, y - 5, pageWidth - 30, 30, 3, 3, 'F');
    doc.setDrawColor(...NAUTIC_GOLD);
    doc.setLineWidth(0.5);
    doc.roundedRect(15, y - 5, pageWidth - 30, 30, 3, 3, 'S');
    
    doc.setTextColor(...NAUTIC_DARK);
    doc.setFontSize(11);
    doc.setFont(undefined, 'bold');
    doc.text(`${bateau.nom}`, 22, y + 5);
    
    doc.setFontSize(9);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text(`${bateau.nom_constructeur} ${bateau.nom_modele} — ${bateau.longueur}m × ${bateau.largeur}m — ${bateau.moteur}`, 22, y + 13);
    
    const dateGen = new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
    doc.text(`Généré le ${dateGen}`, 22, y + 20);
    
    return y + 35;
  };

  const addPageFooter = (doc: jsPDF, pageNum: number, totalPages: number) => {
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    
    doc.setFillColor(...NAUTIC_DARK);
    doc.rect(0, pageHeight - 15, pageWidth, 15, 'F');
    doc.setTextColor(...NAUTIC_GOLD);
    doc.setFontSize(8);
    doc.text('NAUTICOACH — nauticoach.lovable.app', 20, pageHeight - 5);
    doc.setTextColor(...WHITE);
    doc.text(`Page ${pageNum}/${totalPages}`, pageWidth - 20, pageHeight - 5, { align: 'right' });
  };

  const addSectionTitle = (doc: jsPDF, title: string, y: number, count: number): number => {
    const pageWidth = doc.internal.pageSize.width;
    
    doc.setFillColor(...NAUTIC_DARK);
    doc.roundedRect(15, y - 5, pageWidth - 30, 12, 2, 2, 'F');
    
    doc.setTextColor(...NAUTIC_GOLD);
    doc.setFontSize(11);
    doc.setFont(undefined, 'bold');
    doc.text(title, 22, y + 3);
    
    doc.setTextColor(...WHITE);
    doc.setFontSize(9);
    doc.text(`${count} article${count > 1 ? 's' : ''}`, pageWidth - 22, y + 3, { align: 'right' });
    
    return y + 15;
  };

  const addArticleRow = (doc: jsPDF, article: ArticleInventaire, y: number, isEven: boolean, espace?: EspaceStockage): number => {
    const pageWidth = doc.internal.pageSize.width;
    const rowHeight = 10;
    
    if (isEven) {
      doc.setFillColor(248, 249, 252);
      doc.rect(15, y - 4, pageWidth - 30, rowHeight, 'F');
    }
    
    // Nom
    doc.setTextColor(...NAUTIC_DARK);
    doc.setFontSize(9);
    doc.setFont(undefined, 'bold');
    doc.text(article.nom, 22, y + 2);
    
    // Stock avec couleur
    const ratio = article.quantite_max > 0 ? article.quantite / article.quantite_max : 0;
    if (article.quantite === 0) {
      doc.setTextColor(220, 38, 38);
    } else if (ratio <= 0.2) {
      doc.setTextColor(234, 88, 12);
    } else {
      doc.setTextColor(22, 163, 74);
    }
    doc.setFont(undefined, 'normal');
    doc.text(`${article.quantite}/${article.quantite_max} ${article.unite}`, 120, y + 2);
    
    // Emplacement ou sous-catégorie
    doc.setTextColor(120, 120, 120);
    doc.setFontSize(8);
    const info = espace ? espace.nom : (article.sous_categorie || '');
    if (info) {
      doc.text(info, pageWidth - 22, y + 2, { align: 'right' });
    }
    
    return y + rowHeight;
  };

  const checkPageBreak = (doc: jsPDF, y: number, needed: number): number => {
    if (y + needed > 270) {
      doc.addPage();
      return 20;
    }
    return y;
  };

  const generateSecuritePDF = () => {
    setGenerating('securite');
    
    try {
      const articlesSecurite = articles.filter(a => {
        const cat = a.categorie.toLowerCase();
        return cat.includes('sécur') || cat.includes('secur') || cat.includes('équip') || cat.includes('equip');
      });

      const equipements = articlesSecurite.filter(a => {
        const cat = a.categorie.toLowerCase();
        return cat.includes('équip') || cat.includes('equip');
      });
      const securite = articlesSecurite.filter(a => {
        const cat = a.categorie.toLowerCase();
        return cat.includes('sécur') || cat.includes('secur');
      });

      const doc = new jsPDF();
      let y = addHeader(doc, 'ÉQUIPEMENTS & SÉCURITÉ');
      y = addBoatInfo(doc, y);

      // Résumé
      const pageWidth = doc.internal.pageSize.width;
      doc.setFillColor(...NAUTIC_GOLD);
      doc.roundedRect(15, y - 3, pageWidth - 30, 18, 2, 2, 'F');
      doc.setTextColor(...NAUTIC_DARK);
      doc.setFontSize(10);
      doc.setFont(undefined, 'bold');
      doc.text(`Total: ${articlesSecurite.length}`, 22, y + 5);
      doc.setFont(undefined, 'normal');
      doc.text(`Équipements: ${equipements.length}  |  Sécurité: ${securite.length}`, 22, y + 12);
      
      // Articles périmés
      const expires = articlesSecurite.filter(a => a.date_peremption);
      if (expires.length > 0) {
        doc.setTextColor(220, 38, 38);
        doc.text(`${expires.length} article(s) avec date de péremption`, pageWidth - 22, y + 8, { align: 'right' });
      }
      doc.setTextColor(...NAUTIC_DARK);
      y += 25;

      // Section Équipements
      if (equipements.length > 0) {
        y = checkPageBreak(doc, y, 25);
        y = addSectionTitle(doc, 'ÉQUIPEMENTS', y, equipements.length);
        equipements.forEach((article, i) => {
          y = checkPageBreak(doc, y, 12);
          const espace = espacesStockage.find(e => e.id === article.espace_stockage_id);
          y = addArticleRow(doc, article, y, i % 2 === 0, espace);
        });
        y += 10;
      }

      // Section Sécurité
      if (securite.length > 0) {
        y = checkPageBreak(doc, y, 25);
        y = addSectionTitle(doc, 'SÉCURITÉ', y, securite.length);
        securite.forEach((article, i) => {
          y = checkPageBreak(doc, y, 15);
          const espace = espacesStockage.find(e => e.id === article.espace_stockage_id);
          y = addArticleRow(doc, article, y, i % 2 === 0, espace);
          
          // Date péremption
          if (article.date_peremption) {
            doc.setTextColor(234, 88, 12);
            doc.setFontSize(7);
            doc.text(`  ⚠ Expire: ${new Date(article.date_peremption).toLocaleDateString('fr-FR')}`, 30, y);
            y += 5;
          }
        });
      }

      // Footers
      const totalPages = doc.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        addPageFooter(doc, i, totalPages);
      }

      doc.save(`equipements-securite-${new Date().toISOString().split('T')[0]}.pdf`);
      toast({ title: 'PDF généré', description: 'Le PDF Équipements & Sécurité a été téléchargé.' });
    } catch (err) {
      console.error(err);
      toast({ title: 'Erreur', description: 'Erreur lors de la génération du PDF.', variant: 'destructive' });
    } finally {
      setGenerating(null);
    }
  };

  const generateAvitaillementPDF = () => {
    setGenerating('avitaillement');
    
    try {
      // Exclure équipements et sécurité
      const articlesAvitaillement = articles.filter(a => {
        const cat = a.categorie.toLowerCase();
        return !(cat.includes('sécur') || cat.includes('secur') || cat.includes('équip') || cat.includes('equip'));
      });

      // Grouper par catégorie
      const parCategorie: Record<string, ArticleInventaire[]> = {};
      articlesAvitaillement.forEach(a => {
        if (!parCategorie[a.categorie]) parCategorie[a.categorie] = [];
        parCategorie[a.categorie].push(a);
      });

      const doc = new jsPDF();
      let y = addHeader(doc, 'INVENTAIRE AVITAILLEMENT');
      y = addBoatInfo(doc, y);

      // Résumé
      const pageWidth = doc.internal.pageSize.width;
      const nbCategories = Object.keys(parCategorie).length;
      doc.setFillColor(...NAUTIC_GOLD);
      doc.roundedRect(15, y - 3, pageWidth - 30, 18, 2, 2, 'F');
      doc.setTextColor(...NAUTIC_DARK);
      doc.setFontSize(10);
      doc.setFont(undefined, 'bold');
      doc.text(`Total: ${articlesAvitaillement.length} articles`, 22, y + 5);
      doc.setFont(undefined, 'normal');
      doc.text(`${nbCategories} catégorie${nbCategories > 1 ? 's' : ''}`, 22, y + 12);
      
      // Valeur totale
      const valeurTotale = articlesAvitaillement.reduce((sum, a) => {
        return sum + (a.prix_unitaire ? a.prix_unitaire * a.quantite : 0);
      }, 0);
      if (valeurTotale > 0) {
        doc.text(`Valeur estimée: ${valeurTotale.toFixed(2)} €`, pageWidth - 22, y + 8, { align: 'right' });
      }
      y += 25;

      // Par catégorie
      Object.entries(parCategorie).sort(([a], [b]) => a.localeCompare(b)).forEach(([categorie, items]) => {
        y = checkPageBreak(doc, y, 25);
        y = addSectionTitle(doc, categorie.toUpperCase(), y, items.length);
        
        items.forEach((article, i) => {
          y = checkPageBreak(doc, y, 12);
          const espace = espacesStockage.find(e => e.id === article.espace_stockage_id);
          y = addArticleRow(doc, article, y, i % 2 === 0, espace);
        });
        y += 8;
      });

      // Footers
      const totalPages = doc.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        addPageFooter(doc, i, totalPages);
      }

      doc.save(`avitaillement-${new Date().toISOString().split('T')[0]}.pdf`);
      toast({ title: 'PDF généré', description: 'Le PDF Avitaillement a été téléchargé.' });
    } catch (err) {
      console.error(err);
      toast({ title: 'Erreur', description: 'Erreur lors de la génération du PDF.', variant: 'destructive' });
    } finally {
      setGenerating(null);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Choisissez le type de PDF à générer pour <span className="font-semibold text-nautic">{nomBateau || 'votre navire'}</span>.
      </p>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card 
          className="cursor-pointer border-2 hover:border-nautic-gold transition-colors group"
          onClick={() => !generating && generateSecuritePDF()}
        >
          <CardContent className="p-6 text-center space-y-3">
            <div className="mx-auto w-14 h-14 rounded-full bg-nautic/10 flex items-center justify-center group-hover:bg-nautic-gold/20 transition-colors">
              <Shield className="h-7 w-7 text-nautic" />
            </div>
            <h3 className="font-semibold text-nautic text-lg">Équipements & Sécurité</h3>
            <p className="text-sm text-muted-foreground">
              Matériel de sécurité, équipements obligatoires, Division 240
            </p>
            <Button 
              className="w-full bg-nautic-gold text-nautic hover:bg-nautic-lightgold font-semibold"
              disabled={!!generating}
              onClick={(e) => { e.stopPropagation(); generateSecuritePDF(); }}
            >
              {generating === 'securite' ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Génération...</>
              ) : (
                <><FileDown className="h-4 w-4 mr-2" /> Générer le PDF</>
              )}
            </Button>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer border-2 hover:border-nautic-gold transition-colors group"
          onClick={() => !generating && generateAvitaillementPDF()}
        >
          <CardContent className="p-6 text-center space-y-3">
            <div className="mx-auto w-14 h-14 rounded-full bg-nautic/10 flex items-center justify-center group-hover:bg-nautic-gold/20 transition-colors">
              <ShoppingCart className="h-7 w-7 text-nautic" />
            </div>
            <h3 className="font-semibold text-nautic text-lg">Avitaillement</h3>
            <p className="text-sm text-muted-foreground">
              Provisions, consommables, produits d'entretien, etc.
            </p>
            <Button 
              className="w-full bg-nautic-gold text-nautic hover:bg-nautic-lightgold font-semibold"
              disabled={!!generating}
              onClick={(e) => { e.stopPropagation(); generateAvitaillementPDF(); }}
            >
              {generating === 'avitaillement' ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Génération...</>
              ) : (
                <><FileDown className="h-4 w-4 mr-2" /> Générer le PDF</>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default InventairePDFDialog;
